import React from "react";
import getQuantitySigma from "../../../../api/getQuantitySigma";
import { type Quantity } from "../../../../api/useQuantities";
import type { Setup } from "../../../../api/useSetups";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { QuantitySelectorType } from "../../QuantitySelector";
import { useQuantityIndex } from "../../../../api/useQuantities";
import { indexBy } from "diagnostics-lib/iterable";
import { uniqBy } from "diagnostics-lib/array";
import type { Event } from "../../../../api/useEvents";

export const useRevelantQuantities = ({
  dataType,
  event,
  projectId,
  quantities,
  setups,
}: {
  dataType: QuantitySelectorType;
  event: Event;
  projectId: string;
  quantities: Quantity[];
  setups: Setup[];
}) => {
  const quantitiesDAGIndex = useQuantityIndex(projectId);

  const performanceIndicatorsQuantities = React.useMemo(
    () => quantities.filter(f => f.qoiOrder !== null),
    [quantities],
  );

  const indexedPerformanceIndicatorsQuantities = React.useMemo(
    () => indexBy(performanceIndicatorsQuantities, "id"),
    [performanceIndicatorsQuantities],
  );
  const highestNormalizedImpactQuantities = React.useMemo(() => {
    if (event.type === "MANUAL") return [];

    const lastAnalysisDateInEvent = event.endsAt.getTime();

    const setup = getAnalysisSetup(setups, { date: lastAnalysisDateInEvent }) ?? null;
    if (setup === null) return [];

    const failuresByNameAndQuantityId = Object.fromEntries(
      setup.failures.map(failure => [
        failure.name,
        Object.fromEntries(failure.impacts.map(impact => [impact.quantityId, impact])),
      ]),
    );
    const failuresByName = indexBy(setup.failures, "name");

    const highestNormalizedImpactQuantities_ = [];
    for (const quantity of quantities) {
      if (indexedPerformanceIndicatorsQuantities.get(quantity.id)) continue;

      const sigma = getQuantitySigma(setup, quantity.id);
      if (sigma === null) continue;

      const observable = setup.diagnosisInputs.find(f => f.quantityId === quantity.id);
      if (observable && observable.enabled === false) continue;

      const gapImpacts = event.metadata.faults.flatMap(fault => {
        const failure = failuresByName.get(fault.name);
        const impact = failuresByNameAndQuantityId[fault.name]?.[quantity.id]?.value;
        if (
          failure === undefined ||
          impact === undefined ||
          failure.amplitude === undefined ||
          failure.max === undefined ||
          failure.min === undefined
        )
          return [];

        const gapImpactMax = (impact / sigma / failure.amplitude) * failure.max;
        const gapImpactMin = (impact / sigma / failure.amplitude) * failure.min;
        return [Math.max(Math.abs(gapImpactMax), Math.abs(gapImpactMin))];
      });
      if (gapImpacts.length === 0) continue;

      const gapImpact = gapImpacts.reduce(
        (totalImpact, faultImpact) => totalImpact + faultImpact,
        0,
      );
      if (gapImpact < 0.5) continue;

      highestNormalizedImpactQuantities_.push({
        id: quantity.id,
        name: quantity.name,
        impact: gapImpact,
        source: "highestNormalizedImpact",
      });
    }

    return highestNormalizedImpactQuantities_.sort((a, b) => b.impact - a.impact).slice(0, 8);
  }, [event, indexedPerformanceIndicatorsQuantities, quantities, setups]);

  const descendentQuantities = React.useMemo(() => {
    if (dataType !== QuantitySelectorType.Measures) return [];

    const combinedQuantities = [
      ...performanceIndicatorsQuantities,
      ...highestNormalizedImpactQuantities,
    ];
    const descendentQuantities_ = [];
    for (const q of combinedQuantities) {
      const quantity = quantitiesDAGIndex[q.id];
      if (!quantity || quantity.descendants.size === 0) continue;

      for (const desc of quantity.descendants) {
        descendentQuantities_.push({
          id: desc.id,
          name: desc.name,
          gapImpact: null,
          source: "descendent",
        });
      }
    }

    return descendentQuantities_;
  }, [
    dataType,
    performanceIndicatorsQuantities,
    highestNormalizedImpactQuantities,
    quantitiesDAGIndex,
  ]);

  return React.useMemo(
    () =>
      event.type === "DIAGNOSED"
        ? uniqBy(
            [
              ...performanceIndicatorsQuantities,
              ...highestNormalizedImpactQuantities,
              ...descendentQuantities,
            ],
            "id",
          )
        : [],
    [
      performanceIndicatorsQuantities,
      descendentQuantities,
      highestNormalizedImpactQuantities,
      event.type,
    ],
  );
};
